import React, { useMemo, useState, useRef, useEffect } from "react";
import { Modal, Button, Badge } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMinus, faPlus, faTrash, faSpinner } from "@fortawesome/free-solid-svg-icons";
import { Order, OrderItem } from "../OrderList";
// import { toast } from "react-toastify";

interface TentativeItemsEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  order: Order;
  // onSave may be async; modal should close only after this completes successfully
  onSave: (updatedItems: OrderItem[]) => Promise<any> | void;
  cartSummariesData?: any;
  isInventoryLoading?: boolean;
  isDeliveryOrder?: boolean;
  cartId?: string;
  isScheduledOrder?: boolean;
  deliveryDate?: Date | any;
  organizationId?: string | null;
}

const TentativeItemsEditModal: React.FC<TentativeItemsEditModalProps> = ({
  isOpen,
  onClose,
  order,
  onSave,
  cartSummariesData,
  isInventoryLoading,
  isDeliveryOrder,
  cartId,
  isScheduledOrder,
  deliveryDate,
  organizationId,
}) => {
  const [items, setItems] = useState<OrderItem[]>(() => order.orderItems || []);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const mountedRef = useRef<boolean>(true);

  // Inventory check utility function
  const checkInventoryAvailability = (itemId: string, requestedQuantity: number = 1): { available: boolean; availableCount: number; message?: string } => {
    // Skip inventory checks for scheduled orders with future delivery date - manufacturing team will manage future orders
    if (isScheduledOrder && deliveryDate) {
      let deliveryDay: Date;
      
      // Handle Firestore timestamp format
      if (typeof deliveryDate === 'object' && deliveryDate._seconds) {
        deliveryDay = new Date(deliveryDate._seconds * 1000);
      } else {
        deliveryDay = new Date(deliveryDate);
      }
      
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      deliveryDay.setHours(0, 0, 0, 0);
      
      // Skip validation if delivery date is not today
      if (deliveryDay.getTime() !== today.getTime()) {
        return { available: true, availableCount: Infinity };
      }
    }

    if (!cartSummariesData || !cartSummariesData.carts) {
      // No inventory data available, allow the order (fail-open approach)
      return { available: true, availableCount: Infinity };
    }

    let totalAvailable = 0;

    if (isDeliveryOrder) {
      // For delivery orders, sum up available inventory from WebDelivery channel across all carts
      Object.values(cartSummariesData.carts).forEach((cart: any) => {
        if (cart.products && cart.products[itemId]) {
          const product = cart.products[itemId];
          if (product.channels && product.channels.WebDelivery) {
            const available = product.channels.WebDelivery.available || 0;
            totalAvailable += available;
          }
        }
      });
    } else {
      // For pickup orders, check specific cart's WebPickup channel
      if (cartId && cartSummariesData.carts[cartId]) {
        const cart = cartSummariesData.carts[cartId];
        if (cart.products && cart.products[itemId]) {
          const product = cart.products[itemId];
          if (product.channels && product.channels.WebPickup) {
            totalAvailable = product.channels.WebPickup.available || 0;
          }
        }
      }
    }

    const available = totalAvailable >= requestedQuantity;
    let message = '';

    // if (!available) {
    //   if (totalAvailable === 0) {
    //     message = `This item is currently out of stock for ${isDeliveryOrder ? 'delivery' : 'pickup'}.`;
    //   } else {
    //     message = `Only ${totalAvailable} item(s) available for ${isDeliveryOrder ? 'delivery' : 'pickup'}. Please reduce the quantity.`;
    //   }
    // }

    return { available, availableCount: totalAvailable, message };
  };

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const handleDecrease = (index: number) => {
    setItems((prev) => {
      const next = [...prev];
      const current = next[index];
      const newQty = Math.max(0, (current.quantity || 0) - 1);
      if (newQty === 0) {
        next.splice(index, 1);
      } else {
        next[index] = { ...current, quantity: newQty };
      }
      return next;
    });
  };

  const handleIncrease = (index: number) => {
    setItems((prev) => {
      const next = [...prev];
      const current = next[index];
      const newQuantity = (current.quantity || 0) + 1;

      // Check inventory availability before increasing
      // const inventoryCheck = checkInventoryAvailability(current.menuItemId, newQuantity);

      // if (!inventoryCheck.available) {
      //   // Show warning using alert (or integrate with toast if available)
      //   toast.warning(inventoryCheck.message || `Insufficient inventory for ${current.displayName}`);
      //   return prev; // Don't update if insufficient inventory
      // }

      next[index] = { ...current, quantity: newQuantity };
      return next;
    });
  };

  const handleQuantityInput = (
    index: number,
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = Math.max(0, parseInt(event.target.value || "0", 10) || 0);

    setItems((prev) => {
      const next = [...prev];
      // const current = next[index];

      if (value === 0) {
        next.splice(index, 1);
      } else {
        // Check inventory availability before updating quantity
        // const inventoryCheck = checkInventoryAvailability(current.menuItemId, value);

        // if (!inventoryCheck.available) {
        //   // Show warning
        //   alert(inventoryCheck.message || `Insufficient inventory for ${current.displayName}`);
        //   return prev; // Don't update if insufficient inventory
        // }

        next[index] = { ...next[index], quantity: value };
      }
      return next;
    });
  };

  const handleDiscountInput = (
    index: number,
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = Math.max(0, parseFloat(event.target.value || "0") || 0);
    setItems((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], perItemManualDiscount: value };
      return next;
    });
  };

  const hasAnyDiscount = useMemo(
    () => items.some((it) => (it.perItemManualDiscount || 0) > 0),
    [items]
  );

  const totalPrice = useMemo(
    () =>
      items.reduce(
        (sum, it) => {
          // For custom menu items, use unitTotalPrice if available
          const price = (it.itemType === 'CustomMenu' && (it as any).unitTotalPrice)
            ? (it as any).unitTotalPrice
            : (it.unitPrice || 0);
          return sum + price * (it.quantity || 0);
        },
        0
      ),
    [items]
  );

  const handleSave = async () => {
    try {
      setIsSaving(true);
      const result = onSave(items);
      if (result && typeof (result as any).then === "function") {
        await result;
      }
      // Close modal once parent has successfully completed the save (and likely shown toast)
      onClose();
      // If this component is still mounted after onClose, reset saving state (safe-guard)
      if (mountedRef.current) {
        setIsSaving(false);
      }
    } catch (e) {
      // onSave failed — do not close modal so user can correct or retry.
      if (mountedRef.current) {
        setIsSaving(false);
      }
    }
  };

  return (
    <Modal show={isOpen} onHide={onClose} centered size="lg" backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title>Edit Order Items</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="table-responsive">
          <table className="table table-bordered table-sm align-middle">
            <thead className="table-light">
              <tr>
                <th>Item</th>
                <th style={{ width: 150 }}>Quantity</th>
                <th style={{ width: 140 }} className="text-end">
                  Unit Price (Rs.)
                </th>
                {hasAnyDiscount && (
                  <th style={{ width: 140 }} className="text-end">
                    Per Item Discount (Rs.)
                  </th>
                )}
                <th style={{ width: 140 }} className="text-end">
                  Line Total (Rs.)
                </th>
              </tr>
            </thead>
            <tbody>
              {items.map((it, index) => {
                const price = (it.itemType === 'CustomMenu' && (it as any).unitTotalPrice)
                  ? (it as any).unitTotalPrice
                  : (it.unitPrice || 0);
                const quantity = it.quantity || 0;
                const discount = it.perItemManualDiscount || 0;
                const lineTotal = price * quantity;
                const totalDiscount = discount * quantity;
                const finalTotal = lineTotal - totalDiscount;

                return (
                  <tr key={`${it.menuItemId}-${index}`}>
                    <td>
                      <div className="d-flex flex-column">
                        <div className="fw-semibold">{it.displayName}</div>
                        <div className="text-muted" style={{ fontSize: 12 }}>
                          {it.itemType}
                        </div>
                        {/* Inventory Status Badge - Only show for immediate orders */}
                        {!isScheduledOrder && (() => {
                          const inventoryCheck = checkInventoryAvailability(it.menuItemId, 1);
                          const { availableCount } = inventoryCheck;

                          if (availableCount === 0) {
                            return (
                              <Badge bg="danger" className="mt-1" style={{ fontSize: '0.7rem' }}>
                                Out of Stock
                              </Badge>
                            );
                          } else if (availableCount > 0 && availableCount <= 10) {
                            return (
                              <Badge bg="warning" text="dark" className="mt-1" style={{ fontSize: '0.7rem' }}>
                                Low Stock ({availableCount} left)
                              </Badge>
                            );
                          } else if (availableCount > 10) {
                            return (
                              <Badge color="success" className="ms-2" style={{ fontSize: '0.7rem' }}>
                                Stock Available({availableCount} left)
                              </Badge>
                            );
                          }
                          return null;
                        })()}
                      </div>
                    </td>
                    <td>
                      <div className="d-flex align-items-center justify-content-center">
                        <Button
                          className="plus-button"
                          onClick={() => handleDecrease(index)}
                          variant="light"
                          size="sm"
                          title={it.quantity === 1 ? "Remove" : "Decrease"}
                        >
                          <FontAwesomeIcon icon={it.quantity === 1 ? faTrash : faMinus} />
                        </Button>
                        <input
                          type="number"
                          min="0"
                          value={it.quantity ?? 0}
                          onChange={(e) => handleQuantityInput(index, e)}
                          className="quantity-input mx-2 text-center"
                          style={{ width: "80px" }}
                        />
                        <Button
                          className="plus-button"
                          onClick={() => handleIncrease(index)}
                          variant="light"
                          size="sm"
                          title="Increase"
                        >
                          <FontAwesomeIcon icon={faPlus} />
                        </Button>
                      </div>
                    </td>
                    <td className="text-end">
                      {price.toFixed(2)}
                    </td>
                    {hasAnyDiscount && (
                      <td>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={it.perItemManualDiscount || 0}
                          onChange={(e) => handleDiscountInput(index, e)}
                          className="form-control form-control-sm text-end"
                          style={{ width: "100px", margin: "0 auto" }}
                        />
                      </td>
                    )}
                    <td className="text-end">
                      {discount > 0 ? (
                        <div>
                          <div style={{ textDecoration: "line-through", color: "#999", fontSize: "0.85em" }}>
                            {lineTotal.toFixed(2)}
                          </div>
                          <div style={{ color: "#28a745", fontWeight: "bold" }}>
                            {finalTotal.toFixed(2)}
                          </div>
                        </div>
                      ) : (
                        lineTotal.toFixed(2)
                      )}
                    </td>
                  </tr>
                );
              })}
              {items.length === 0 && (
                <tr>
                  <td colSpan={hasAnyDiscount ? 5 : 4} className="text-center text-muted">
                    No items in this order.
                  </td>
                </tr>
              )}
            </tbody>
            <tfoot>
              <tr className="fw-bold">
                <td colSpan={hasAnyDiscount ? 4 : 3} className="text-end">
                  Total:
                </td>
                <td className="text-end">{totalPrice.toFixed(2)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose} disabled={isSaving}>
          Cancel
        </Button>
        <Button variant="danger" onClick={handleSave} disabled={isSaving}>
          {isSaving ? (
            <>
              <FontAwesomeIcon icon={faSpinner} spin /> Saving...
            </>
          ) : (
            "Save Changes"
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default TentativeItemsEditModal;



